import { create } from 'zustand'
import axios from 'axios'

const API_BASE = import.meta.env.PROD ? '/api' : 'http://localhost:9099/api'

interface BarData {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

interface SpreadData {
  bid: number
  ask: number
  spread: number
  timestamp: number
}

interface PositionLot {
  client_order_id: string
  quantity: number
  entry_price: number | null
  entry_time: number | null
  tp_price: number | null
  sl_price: number | null
  unrealized_pnl: number | null
  inferred: boolean
}

interface Position {
  instrument_id: string
  side: string | null
  quantity: number
  avg_entry_price: number
  unrealized_pnl: number
  realized_pnl: number
  ts_opened?: number
  lots?: PositionLot[]
}

interface Trade {
  type: string
  side: string
  price: number
  quantity?: number
  timestamp: number
  order_id?: string
}

// Past trades (last ~24h) from the backend /trades endpoint (IBKR executions via reqExecutions).
interface TradeHistoryItem {
  timestamp: string
  instrument_id: string
  side: string
  quantity: number
  price: number
  commission: number
  order_id: string
  pnl: number | null  // IB-reported realized PnL for closing fills; null for opening fills
}

interface Order {
  client_order_id: string
  instrument_id: string
  order_type: string
  side: string
  quantity: number
  price: number | null
  trigger_price: number | null
  status: string
  tags: string[]
  ts_init: number
  parent_order_id?: string | null
  is_reduce_only?: boolean
}

interface Alert {
  level: string
  message: string
  timestamp: string
}

interface LogEntry {
  level: 'debug' | 'info' | 'warning' | 'error'
  message: string
  timestamp: string
  source?: string
}

interface Metrics {
  pnl: number
  realized_pnl: number
  unrealized_pnl: number
  sharpe_ratio: number | null
  max_drawdown: number | null  // null in live mode (no equity-curve tracking)
  win_rate: number
  total_trades: number
  profitable_trades: number
  losing_trades: number
  avg_win: number
  avg_loss: number
  profit_factor: number | null  // null when no losing trades yet (live)
}

interface Indicators {
  ema_fast?: number
  ema_slow?: number
  macd?: {
    value: number
    signal: number
    histogram: number
  }
}

interface StrategyInfo {
  id: string
  name: string
  description: string
  presets: string[]
  default_preset: string
  default_instrument: string
  default_instrument_live?: string
}

interface ActiveStrategyInfo {
  strategy_id: string
  preset_name: string
  is_running: boolean
  preset_params?: Record<string, any>
  // Static choices per parameter (the server's PARAM_OPTIONS): rendered as dropdowns.
  param_options?: Record<string, any[]>
  // The parameter edits the current/last run was started with (every mode).
  param_overrides?: Record<string, any> | null
  run_mode?: string
  instrument?: string | null
  started_at?: string | null
}

interface BacktestInstrument {
  id: string
  name: string
  startDate: string
  endDate: string
  barCount?: number
  // Data-quality audit reason code, or null when clean. Set by the
  // /backtest/instruments route from utils/data_quality.py.
  dataQuality?: string | null
  // Explicit audit verdict from the same route. Needed because dataQuality is
  // null both for a clean contract and for one the audit never saw, so it
  // cannot answer "should a select-all pick this?".
  dataQualityStatus?: 'usable' | 'caveat' | 'excluded' | 'unaudited' | null
}

interface MarketAnalysisInstrument {
  id: string
  name: string
  start_date: string
  end_date: string
  total_return_pct: number
  max_drawdown_pct: number
  volatility_ann_pct: number
  trend: 'bullish' | 'bearish' | 'neutral'
  bar_count: number
  trading_days: number
}

interface MarketAnalysisGroup {
  root_symbol: string
  instruments: MarketAnalysisInstrument[]
  avg_return_pct: number
  most_bullish: { id: string; total_return_pct: number } | null
  most_bearish: { id: string; total_return_pct: number } | null
}

interface MarketAnalysisData {
  groups: Record<string, MarketAnalysisGroup>
  all_instruments: MarketAnalysisInstrument[]
  computed_at: string
}

interface BacktestConfig {
  instrument: string
  startDate: string
  endDate: string
}

interface BacktestTrade {
  timestamp: string
  side: string
  price: number
  quantity: number
  pnl: number
}

interface BacktestResults {
  backtest_id: string
  totalTrades: number
  // Positions still open when the data ended — NOT completed trades, and
  // excluded from totalTrades/winRate. Surfaced so a run that simply never
  // closed its losers cannot look like a clean one.
  openPositions: number
  winRate: number
  pnl: number
  maxDrawdown: number
  sharpeRatio: number | null
  profitFactor: number
  trades: BacktestTrade[]
  equityCurve: { time: string; equity: number }[]
}

interface BacktestSummary {
  backtest_id: string
  strategy_id: string
  preset_name: string
  instrument: string
  status: string
  started_at: string
  completed_at: string | null
  result_type: 'backtest' | 'optimization' | 'multi_backtest'
  summary: {
    pnl: number | null
    total_trades: number | null
    win_rate: number | null
  } | null
  error: string | null
  // Multi-backtest fields
  multi_backtest_id?: string
  total_instruments?: number
  completed_count?: number
}

interface OptimizationResult {
  [param: string]: any
  net_return: number
  max_drawdown: string
  final_balance: number
}

interface MultiBacktestHeatmapRow {
  strategy_id: string
  preset_name: string
  instrument: string
  pnl: number
  net_return: number
  win_rate: number
  max_drawdown: number
  sharpe_ratio: number | null
  profit_factor: number
  total_trades: number
}

interface MultiBacktestResults {
  multi_backtest_id: string
  strategy_id: string
  preset_name: string
  status: string
  started_at: string
  completed_at: string | null
  instruments: string[]
  heatmap_data: MultiBacktestHeatmapRow[]
  errors: Record<string, string>
  summary: Record<string, number | null>
  total_instruments: number
  completed_count: number
  failed_count: number
  strategy_presets: Array<{ strategy_id: string; preset: string }> | null
  is_multi_strategy: boolean
}

interface MultiBacktestProgress {
  multi_backtest_id: string
  currentStrategy: string | null
  currentPreset: string | null
  currentInstrument: string
  // Jobs run concurrently, so this is a monotonic COMPLETED count, not a
  // position in the job list, and currentInstrument is the one that just
  // finished. runningInstruments is what is executing right now.
  currentIndex: number
  totalInstruments: number
  runningInstruments: string[]
  completedResults: Record<string, any>
  errors: Record<string, string>
}

interface TradingState {
  // Strategy status
  strategyStatus: 'running' | 'stopped' | 'warning'
  strategyStartedAt: string | null

  // Equity curve (persisted across navigation)
  equityCurve: { time: string; equity: number; pnl: number }[]

  // Account
  accountBalance: number | null
  accountCurrency: string | null
  marginInitial: number | null
  marginMaintenance: number | null

  // Market data
  bars: BarData[]
  currentBar: BarData | null
  spreadData: SpreadData | null

  // Positions
  positions: Position[]

  // Trading
  trades: Trade[]
  tradeHistory: TradeHistoryItem[]
  orders: Order[]

  // Metrics
  metrics: Metrics

  // Indicators
  indicators: Indicators

  // Alerts
  alerts: Alert[]

  // Strategy logs
  logs: LogEntry[]

  // Parameters
  strategyParams: {
    stop_loss_percent: number
    take_profit_percent: number
    trailing_stop_percent: number
    max_position_size: number
    min_volume_a: number
    min_volume_b: number
  }

  // Strategy selection
  availableStrategies: StrategyInfo[]
  activeStrategy: ActiveStrategyInfo | null
  selectedStrategyId: string | null
  selectedPreset: string | null

  // Run mode
  runMode: 'live' | 'backtest' | 'idle'

  // Backtest
  backtestInstruments: BacktestInstrument[]
  backtestConfig: BacktestConfig | null
  backtestStatus: 'idle' | 'running' | 'completed' | 'error'
  backtestProgress: number
  backtestResults: BacktestResults | null
  backtestError: string | null
  paramOverrides: Record<string, any> | null

  // Backtest History
  backtestHistory: BacktestSummary[]
  backtestHistoryLoading: boolean
  selectedHistoryBacktestId: string | null

  // Optimization
  optimizationStatus: 'idle' | 'running' | 'completed' | 'error'
  optimizationProgress: { current: number; total: number } | null
  optimizationResults: OptimizationResult[] | null
  optimizationError: string | null
  optimizationParamGrid: Record<string, any[]> | null
  optimizationTotalCombinations: number
  optimizationMethod: 'grid' | 'optuna'
  optunaConfig: { n_trials: number; walk_forward_splits: number; train_ratio: number }
  optimizationParetoFront: OptimizationResult[] | null
  optimizationInstruments: string[]

  // Data Catalog Configuration
  dataCatalogPath: string
  dataCatalogExists: boolean
  dataCatalogDefaultPath: string
  dataCatalogLoading: boolean
  dataCatalogError: string | null

  // Data Quality Audit
  dataAuditRunning: boolean
  dataAuditProgress: { step: string; current: number; total: number } | null
  dataAuditSummary: { usable: number; caveat: number; notSuitable: number; total: number } | null
  dataAuditError: string | null

  // Market Analysis
  marketAnalysis: MarketAnalysisData | null
  marketAnalysisLoading: boolean
  marketAnalysisError: string | null

  // Multi-instrument/multi-strategy backtest
  multiBacktestInstruments: string[]
  // Concurrent backtest worker processes. Seeded from the server's
  // default_workers() on first instrument fetch; 1 = old sequential path.
  multiBacktestWorkers: number
  multiBacktestWorkersTouched: boolean
  multiBacktestStrategyPresets: Array<{ strategy_id: string; preset: string }>
  multiBacktestStatus: 'idle' | 'running' | 'completed' | 'error'
  multiBacktestId: string | null
  multiBacktestProgress: MultiBacktestProgress | null
  multiBacktestResults: MultiBacktestResults | null
  multiBacktestError: string | null

  // Actions
  updateAccountBalance: (balance: number, currency: string, marginInitial: number, marginMaintenance: number) => void
  updateBar: (bar: BarData) => void
  updateSpread: (spread: SpreadData) => void
  updatePosition: (position: Position) => void
  updateMetrics: (metrics: Partial<Metrics>) => void
  updateIndicator: (name: string, data: any) => void
  addTrade: (trade: Trade) => void
  addAlert: (alert: Alert) => void
  clearAlerts: () => void
  addLog: (log: LogEntry) => void
  clearLogs: () => void
  setStrategyStatus: (status: 'running' | 'stopped' | 'warning') => void
  setStrategyStartedAt: (ts: string | null) => void
  fetchEquityCurve: (signal?: AbortSignal) => Promise<void>

  // API actions
  startStrategy: () => Promise<void>
  stopStrategy: () => Promise<void>
  emergencyClose: () => Promise<void>
  updateStrategyParams: (params: Partial<TradingState['strategyParams']>) => Promise<void>
  fetchChartBars: () => Promise<void>
  fetchMetrics: (signal?: AbortSignal) => Promise<void>
  fetchPositions: () => Promise<void>
  fetchOrders: () => Promise<void>
  fetchTrades: () => Promise<void>

  // Strategy selection actions
  fetchStrategies: () => Promise<void>
  selectStrategy: (strategyId: string, preset: string) => Promise<void>
  startSelectedStrategy: (instrument?: string) => Promise<void>
  stopSelectedStrategy: () => Promise<void>
  setSelectedStrategyId: (id: string | null) => void
  setSelectedPreset: (preset: string | null) => void

  // Run mode actions
  setRunMode: (mode: 'live' | 'backtest' | 'idle') => void

  // Backtest actions
  fetchBacktestInstruments: (forceRefresh?: boolean) => Promise<void>
  setBacktestConfig: (config: BacktestConfig | null) => void
  setParamOverrides: (overrides: Record<string, any> | null) => void
  runBacktest: () => Promise<void>
  clearBacktestResults: () => void

  // Backtest History actions
  fetchBacktestHistory: () => Promise<void>
  loadBacktestResult: (backtestId: string) => Promise<void>
  deleteBacktestResult: (backtestId: string) => Promise<void>
  setSelectedHistoryBacktestId: (id: string | null) => void

  // Optimization actions
  fetchParamGrid: () => Promise<void>
  runOptimization: (paramGrid?: Record<string, any[]>) => Promise<void>
  clearOptimizationResults: () => void
  updateOptimizationProgress: (data: { current: number; total: number; result?: OptimizationResult }) => void
  setOptimizationMethod: (method: 'grid' | 'optuna') => void
  setOptunaConfig: (config: Partial<{ n_trials: number; walk_forward_splits: number; train_ratio: number }>) => void
  setOptimizationInstruments: (ids: string[]) => void

  // Data Catalog actions
  fetchDataCatalogConfig: () => Promise<void>
  setDataCatalogPath: (path: string) => Promise<boolean>
  scanDataCatalog: () => Promise<void>
  runDataAudit: () => Promise<void>

  // Market Analysis actions
  fetchMarketAnalysis: (forceRefresh?: boolean) => Promise<void>
  clearMarketAnalysis: () => void

  // Multi-backtest actions
  setMultiBacktestInstruments: (ids: string[]) => void
  setMultiBacktestWorkers: (n: number) => void
  setMultiBacktestStrategyPresets: (presets: Array<{ strategy_id: string; preset: string }>) => void
  runMultiBacktest: () => Promise<void>
  fetchMultiBacktestResults: (id: string) => Promise<void>
  clearMultiBacktestResults: () => void
}

// The Strategy Parameters edits are a diff against the LOADED preset
// (activeStrategy). Running a different strategy/preset with them would apply
// that diff to the wrong base, so such a run is refused with an explanation.
function paramEditsMismatch(state: TradingState): string | null {
  const ov = state.paramOverrides
  if (!ov || Object.keys(ov).length === 0 || !state.activeStrategy) return null
  const { strategy_id, preset_name } = state.activeStrategy
  if (strategy_id === state.selectedStrategyId && preset_name === state.selectedPreset) return null
  return (
    `Parameter edits belong to ${preset_name}; click Load to switch to ` +
    `${state.selectedPreset} (this discards the edits) or Reset to Preset`
  )
}

export const useTradingStore = create<TradingState>((set, get) => ({
  // Initial state
  strategyStatus: 'stopped',
  strategyStartedAt: null,
  equityCurve: [],

  accountBalance: null,
  accountCurrency: null,
  marginInitial: null,
  marginMaintenance: null,

  bars: [],
  currentBar: null,
  spreadData: null,

  positions: [],

  trades: [],
  tradeHistory: [],
  orders: [],

  metrics: {
    pnl: 0,
    realized_pnl: 0,
    unrealized_pnl: 0,
    sharpe_ratio: null,
    max_drawdown: 0,
    win_rate: 0,
    total_trades: 0,
    profitable_trades: 0,
    losing_trades: 0,
    avg_win: 0,
    avg_loss: 0,
    profit_factor: 0,
  },

  indicators: {},

  alerts: [],

  logs: [],

  strategyParams: {
    stop_loss_percent: 2.0,
    take_profit_percent: 4.0,
    trailing_stop_percent: 1.5,
    max_position_size: 5,
    min_volume_a: 100,
    min_volume_b: 100,
  },

  // Strategy selection initial state
  availableStrategies: [],
  activeStrategy: null,
  selectedStrategyId: null,
  selectedPreset: null,

  // Run mode initial state
  runMode: 'idle',

  // Backtest initial state
  backtestInstruments: [],
  backtestConfig: null,
  backtestStatus: 'idle',
  backtestProgress: 0,
  backtestResults: null,
  backtestError: null,
  paramOverrides: null,

  // Backtest History initial state
  backtestHistory: [],
  backtestHistoryLoading: false,
  selectedHistoryBacktestId: null,

  // Optimization initial state
  optimizationStatus: 'idle',
  optimizationProgress: null,
  optimizationResults: null,
  optimizationError: null,
  optimizationParamGrid: null,
  optimizationTotalCombinations: 0,
  optimizationMethod: 'grid',
  optunaConfig: { n_trials: 100, walk_forward_splits: 0, train_ratio: 0.7 },
  optimizationParetoFront: null,
  optimizationInstruments: [],

  // Data Catalog initial state
  dataCatalogPath: '',
  dataCatalogExists: false,
  dataCatalogDefaultPath: '',
  dataCatalogLoading: false,
  dataCatalogError: null,

  // Data Quality Audit initial state
  dataAuditRunning: false,
  dataAuditProgress: null,
  dataAuditSummary: null,
  dataAuditError: null,

  // Market Analysis initial state
  marketAnalysis: null,
  marketAnalysisLoading: false,
  marketAnalysisError: null,

  // Multi-backtest initial state
  multiBacktestInstruments: [],
  multiBacktestStrategyPresets: [],
  multiBacktestStatus: 'idle',
  multiBacktestId: null,
  multiBacktestWorkers: 4,
  multiBacktestWorkersTouched: false,
  multiBacktestProgress: null,
  multiBacktestResults: null,
  multiBacktestError: null,

  // Actions
  updateBar: (bar) => {
    set((state) => {
      const prev = state.bars.slice(-499)
      const last = prev[prev.length - 1]
      const updated =
        last && last.timestamp === bar.timestamp
          ? [...prev.slice(0, -1), bar]  // replace bar with same timestamp
          : [...prev, bar]
      return { bars: updated, currentBar: bar }
    })
  },

  updateAccountBalance: (balance, currency, marginInitial, marginMaintenance) => {
    set({ accountBalance: balance, accountCurrency: currency, marginInitial, marginMaintenance })
  },

  updateSpread: (spread) => {
    set({ spreadData: spread })
  },

  updatePosition: (position) => {
    set((state) => {
      const existingIndex = state.positions.findIndex(
        (p) => p.instrument_id === position.instrument_id
      )
      if (existingIndex >= 0) {
        const newPositions = [...state.positions]
        newPositions[existingIndex] = position
        return { positions: newPositions }
      }
      return { positions: [...state.positions, position] }
    })
  },

  updateMetrics: (metrics) => {
    set((state) => ({
      metrics: { ...state.metrics, ...metrics },
    }))
  },

  updateIndicator: (name, data) => {
    set((state) => ({
      indicators: { ...state.indicators, [name]: data },
    }))
  },

  addTrade: (trade) => {
    set((state) => ({
      trades: [...state.trades.slice(-99), trade],
    }))
  },

  addAlert: (alert) => {
    set((state) => ({
      alerts: [...state.alerts.slice(-49), alert],
    }))
  },

  clearAlerts: () => {
    set({ alerts: [] })
  },

  addLog: (log) => {
    set((state) => ({
      logs: [...state.logs.slice(-499), log],
    }))
  },

  clearLogs: () => {
    set({ logs: [] })
  },

  setStrategyStatus: (status) => {
    set({ strategyStatus: status })
  },

  setStrategyStartedAt: (ts) => {
    set({ strategyStartedAt: ts })
  },

  fetchEquityCurve: async (signal?: AbortSignal) => {
    try {
      const response = await axios.get(`${API_BASE}/equity-curve`, {
        params: { resolution: 'minute', limit: 500 },
        signal,
      })
      // Server timestamps are naive UTC — append 'Z' so they render as local time
      const points = (response.data.points ?? []).map(
        (p: { timestamp: string; equity: number; pnl: number }) => ({
          time: new Date(p.timestamp + 'Z').toLocaleTimeString(),
          equity: p.equity,
          pnl: p.pnl,
        })
      )
      set({ equityCurve: points })
    } catch (error) {
      console.error('Failed to fetch equity curve:', error)
    }
  },

  // API actions
  startStrategy: async () => {
    try {
      await axios.post(`${API_BASE}/strategy/start`)
      set({ strategyStatus: 'running' })
    } catch (error) {
      console.error('Failed to start strategy:', error)
      throw error
    }
  },

  stopStrategy: async () => {
    try {
      await axios.post(`${API_BASE}/strategy/stop`)
      set({ strategyStatus: 'stopped' })
    } catch (error) {
      console.error('Failed to stop strategy:', error)
      throw error
    }
  },

  emergencyClose: async () => {
    try {
      await axios.post(`${API_BASE}/strategy/emergency-close`)
      set({ strategyStatus: 'warning' })
    } catch (error) {
      console.error('Emergency close failed:', error)
      throw error
    }
  },

  updateStrategyParams: async (params) => {
    try {
      await axios.put(`${API_BASE}/strategy/params`, params)
      set((state) => ({
        strategyParams: { ...state.strategyParams, ...params },
      }))
    } catch (error) {
      console.error('Failed to update params:', error)
      throw error
    }
  },

  fetchChartBars: async () => {
    try {
      const response = await axios.get(`${API_BASE}/market/chart-bars`, { params: { limit: 500 } })
      const bars: BarData[] = (response.data.bars ?? [])
        .sort((a: BarData, b: BarData) => a.timestamp - b.timestamp)
      if (bars.length > 0) {
        set({ bars, currentBar: bars[bars.length - 1] })
      }
    } catch (error) {
      console.error('Failed to fetch chart bars:', error)
    }
  },

  fetchMetrics: async (signal?: AbortSignal) => {
    try {
      const response = await axios.get(`${API_BASE}/metrics`, { signal })
      const { account_balance, account_currency, margin_initial, margin_maintenance, timestamp: _ts, ...metrics } = response.data
      set({ metrics })
      if (account_balance != null) {
        get().updateAccountBalance(account_balance, account_currency, margin_initial ?? 0, margin_maintenance ?? 0)
      }
    } catch (error: any) {
      if (axios.isCancel(error) || error?.name === 'AbortError') return
      console.error('Failed to fetch metrics:', error)
    }
  },

  fetchPositions: async () => {
    try {
      const response = await axios.get(`${API_BASE}/positions`)
      set({ positions: response.data.positions })
    } catch (error) {
      console.error('Failed to fetch positions:', error)
    }
  },

  fetchOrders: async () => {
    try {
      const response = await axios.get(`${API_BASE}/orders`)
      set({ orders: response.data.orders ?? [] })
    } catch (error) {
      console.error('Failed to fetch orders:', error)
    }
  },

  fetchTrades: async () => {
    try {
      const response = await axios.get(`${API_BASE}/trades`)
      set({ tradeHistory: response.data.trades ?? [] })
    } catch (error) {
      console.error('Failed to fetch trades:', error)
    }
  },

  // Strategy selection actions
  fetchStrategies: async () => {
    try {
      const response = await axios.get(`${API_BASE}/strategies`)
      set({
        availableStrategies: response.data.strategies,
        activeStrategy: response.data.active_strategy,
      })

      // Sync selected state with active strategy
      if (response.data.active_strategy) {
        const active = response.data.active_strategy
        // Mirror the backend's run_mode onto our tri-state. Optimizer/sandbox
        // runs render as "Backtest - Running" in the header for now.
        const hydratedRunMode: TradingState['runMode'] | null =
          active.is_running && active.run_mode
            ? (active.run_mode === 'live' ? 'live' : 'backtest')
            : null
        set({
          selectedStrategyId: active.strategy_id,
          selectedPreset: active.preset_name,
          strategyStatus: active.is_running ? 'running' : 'stopped',
          strategyStartedAt: active.is_running ? (active.started_at ?? null) : null,
          ...(hydratedRunMode ? { runMode: hydratedRunMode } : {}),
        })
      }
    } catch (error) {
      console.error('Failed to fetch strategies:', error)
    }
  },

  selectStrategy: async (strategyId: string, preset: string) => {
    try {
      const response = await axios.post(`${API_BASE}/strategies/select`, {
        strategy_id: strategyId,
        preset: preset
      })
      set({
        selectedStrategyId: strategyId,
        selectedPreset: preset,
        activeStrategy: {
          strategy_id: strategyId,
          preset_name: preset,
          is_running: false,
          preset_params: response.data.preset_params || undefined,
          param_options: response.data.param_options || undefined,
        },
        // The edits were a diff against the previously loaded preset.
        paramOverrides: null,
      })
      return response.data
    } catch (error) {
      console.error('Failed to select strategy:', error)
      throw error
    }
  },

  startSelectedStrategy: async (instrument?: string) => {
    const state = get()
    const { runMode } = state
    const mode = runMode === 'idle' ? 'backtest' : runMode
    // Live and sandbox honour the Backtest tab's parameter edits like a backtest
    // does; refuse them when they belong to another preset.
    const mismatch = paramEditsMismatch(state)
    if (mismatch) throw new Error(mismatch)
    const overrides =
      state.paramOverrides && Object.keys(state.paramOverrides).length > 0 ? state.paramOverrides : null
    try {
      const payload: Record<string, any> = { mode }
      if (instrument) payload.instrument = instrument
      if (overrides) payload.param_overrides = overrides
      await axios.post(`${API_BASE}/strategies/start`, payload)
      set((state) => ({
        activeStrategy: state.activeStrategy
          ? { ...state.activeStrategy, is_running: true, param_overrides: overrides }
          : null,
        strategyStatus: 'running',
        strategyStartedAt: new Date().toISOString(),
        equityCurve: [],
        // Reset metrics so Dashboard shows fresh data for this run
        metrics: {
          pnl: 0,
          realized_pnl: 0,
          unrealized_pnl: 0,
          sharpe_ratio: null,
          max_drawdown: 0,
          win_rate: 0,
          total_trades: 0,
          profitable_trades: 0,
          losing_trades: 0,
          avg_win: 0,
          avg_loss: 0,
          profit_factor: 0,
        },
        trades: [],
        positions: [],
        indicators: {},
      }))
    } catch (error) {
      console.error('Failed to start strategy:', error)
      throw error
    }
  },

  stopSelectedStrategy: async () => {
    try {
      await axios.post(`${API_BASE}/strategies/stop`)
      set((state) => ({
        activeStrategy: state.activeStrategy
          ? { ...state.activeStrategy, is_running: false }
          : null,
        strategyStatus: 'stopped',
        strategyStartedAt: null,
      }))
    } catch (error) {
      console.error('Failed to stop strategy:', error)
      throw error
    }
  },

  setSelectedStrategyId: (id: string | null) => {
    set({ selectedStrategyId: id })
  },

  setSelectedPreset: (preset: string | null) => {
    set({ selectedPreset: preset })
  },

  // Run mode actions
  setRunMode: (mode: 'live' | 'backtest' | 'idle') => {
    set({ runMode: mode })
  },

  // Backtest actions
  fetchBacktestInstruments: async (forceRefresh: boolean = false) => {
    // Skip if already loaded and not forcing refresh
    const currentInstruments = get().backtestInstruments
    if (!forceRefresh && currentInstruments.length > 0) {
      return
    }

    try {
      const response = await axios.get(`${API_BASE}/backtest/instruments`, {
        params: { refresh: forceRefresh }
      })
      // Seed the worker count from the server's CPU-derived default, but never
      // clobber a value the user has already chosen this session.
      const serverDefault = response.data.default_workers
      const patch: Record<string, unknown> = {
        backtestInstruments: response.data.instruments || [],
        dataCatalogPath: response.data.catalog_path || '',
        dataCatalogExists: (response.data.instruments?.length || 0) > 0,
      }
      if (typeof serverDefault === 'number' && !get().multiBacktestWorkersTouched) {
        patch.multiBacktestWorkers = Math.max(1, Math.min(16, serverDefault))
      }
      set(patch)
    } catch (error) {
      console.error('Failed to fetch backtest instruments:', error)
      set({ backtestInstruments: [] })
    }
  },

  setBacktestConfig: (config: BacktestConfig | null) => {
    set({ backtestConfig: config, backtestResults: null, backtestError: null })
  },

  setParamOverrides: (overrides: Record<string, any> | null) => {
    set({ paramOverrides: overrides })
  },

  runBacktest: async () => {
    const state = get()
    if (!state.backtestConfig || !state.selectedStrategyId || !state.selectedPreset) {
      set({ backtestError: 'Please select a strategy and configure backtest parameters' })
      return
    }
    // The edits are a diff against the LOADED preset; running another preset
    // with them would apply that diff to the wrong base.
    const mismatch = paramEditsMismatch(state)
    if (mismatch) {
      set({ backtestError: mismatch })
      return
    }

    set({ backtestStatus: 'running', backtestProgress: 0, backtestError: null })

    try {
      // First ensure the strategy is selected in the registry
      await axios.post(`${API_BASE}/strategies/select`, {
        strategy_id: state.selectedStrategyId,
        preset: state.selectedPreset,
      })

      // Start the strategy in backtest mode using the strategy registry
      const response = await axios.post(`${API_BASE}/strategies/start`, {
        mode: 'backtest',
        instrument: state.backtestConfig.instrument,
        ...(state.paramOverrides && Object.keys(state.paramOverrides).length > 0
          ? { param_overrides: state.paramOverrides }
          : {}),
      })

      // Poll for results if we get a backtest_id
      const backtestId = response.data.backtest_id
      if (backtestId) {
        // Use elapsed time to drive a logarithmic progress curve that
        // slows down as it approaches 95%, so the bar never stalls at a fixed value.
        const startTime = Date.now()
        const progressInterval = setInterval(() => {
          const elapsed = (Date.now() - startTime) / 1000
          // Logarithmic curve: fast initially, gradually slows toward 95%
          // At 5s ~60%, at 15s ~78%, at 30s ~85%, at 60s ~90%, at 120s ~93%
          const progress = Math.min(95, Math.round(95 * (1 - Math.exp(-elapsed / 15))))
          set({ backtestProgress: progress })
        }, 300)

        // Poll for completion
        const pollResults = async () => {
          try {
            const resultsResponse = await axios.get(`${API_BASE}/backtest/results/${backtestId}`)
            if (resultsResponse.data.status === 'completed') {
              clearInterval(progressInterval)
              const results = resultsResponse.data.results || {}
              set({
                backtestStatus: 'completed',
                backtestProgress: 100,
                backtestResults: {
                  backtest_id: backtestId,
                  totalTrades: results.total_trades || 0,
                  openPositions: results.open_positions || 0,
                  winRate: results.win_rate || 0,
                  pnl: results.pnl || 0,
                  maxDrawdown: results.max_drawdown || 0,
                  sharpeRatio: results.sharpe_ratio || null,
                  profitFactor: results.profit_factor || 0,
                  trades: results.trades || [],
                  equityCurve: results.equity_curve || [],
                }
              })
            } else if (resultsResponse.data.status === 'error') {
              clearInterval(progressInterval)
              set({
                backtestStatus: 'error',
                backtestError: resultsResponse.data.error || 'Backtest failed'
              })
            } else {
              // Still running, poll again
              setTimeout(pollResults, 1000)
            }
          } catch (pollError: any) {
            // If 404, backtest might still be running - retry
            if (pollError.response?.status === 404) {
              setTimeout(pollResults, 1000)
              return
            }
            clearInterval(progressInterval)
            set({
              backtestStatus: 'error',
              backtestError: pollError.response?.data?.detail || 'Failed to fetch results'
            })
          }
        }

        // Start polling after a short delay
        setTimeout(pollResults, 1000)
      }
    } catch (error: any) {
      set({
        backtestStatus: 'error',
        backtestError: error.response?.data?.detail || 'Failed to run backtest'
      })
    }
  },

  clearBacktestResults: () => {
    set({
      backtestResults: null,
      backtestStatus: 'idle',
      backtestProgress: 0,
      backtestError: null,
      selectedHistoryBacktestId: null,
    })
  },

  // Backtest History actions
  fetchBacktestHistory: async () => {
    set({ backtestHistoryLoading: true })
    try {
      const [btResponse, multiResponse] = await Promise.all([
        axios.get(`${API_BASE}/backtest/results`),
        axios.get(`${API_BASE}/backtest/multi-results`).catch(() => ({ data: { multi_backtests: [] } })),
      ])

      const regularEntries: BacktestSummary[] = btResponse.data.backtests || []

      // Convert multi-backtest entries into the same format for the sidebar
      const multiEntries: BacktestSummary[] = (multiResponse.data.multi_backtests || []).map((mb: any) => ({
        backtest_id: mb.multi_backtest_id,
        strategy_id: mb.strategy_id,
        preset_name: mb.preset_name,
        instrument: `${mb.completed_count}/${mb.total_instruments} instruments`,
        status: mb.status,
        started_at: mb.started_at,
        completed_at: mb.completed_at,
        result_type: 'multi_backtest' as const,
        summary: { pnl: mb.total_pnl, total_trades: null, win_rate: null },
        error: null,
        multi_backtest_id: mb.multi_backtest_id,
        total_instruments: mb.total_instruments,
        completed_count: mb.completed_count,
      }))

      // Merge and sort by started_at descending
      const merged = [...regularEntries, ...multiEntries].sort(
        (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
      )

      set({
        backtestHistory: merged,
        backtestHistoryLoading: false,
      })
    } catch (error) {
      console.error('Failed to fetch backtest history:', error)
      set({ backtestHistoryLoading: false })
    }
  },

  loadBacktestResult: async (backtestId: string) => {
    try {
      // Handle multi-backtest IDs
      if (backtestId.startsWith('mbt_')) {
        const res = await axios.get(`${API_BASE}/backtest/multi-results/${backtestId}`)
        set({
          multiBacktestStatus: 'completed',
          multiBacktestResults: res.data,
          multiBacktestError: null,
          // Clear single backtest / optimization state
          backtestStatus: 'idle',
          backtestResults: null,
          backtestError: null,
          optimizationStatus: 'idle',
          optimizationResults: null,
          selectedHistoryBacktestId: backtestId,
        })
        return
      }

      const response = await axios.get(`${API_BASE}/backtest/results/${backtestId}`)
      if (response.data.status === 'completed' && response.data.results) {
        const results = response.data.results

        if (results.type === 'optimization') {
          // This is an optimization result — populate optimization state
          set({
            optimizationStatus: 'completed',
            optimizationResults: results.results || [],
            optimizationProgress: {
              current: results.total_combinations || 0,
              total: results.total_combinations || 0,
            },
            optimizationParetoFront: results.pareto_front || null,
            optimizationError: null,
            // Clear single backtest state
            backtestStatus: 'idle',
            backtestResults: null,
            backtestError: null,
            selectedHistoryBacktestId: backtestId,
          })
        } else {
          // Single backtest result
          set({
            backtestStatus: 'completed',
            backtestResults: {
              backtest_id: backtestId,
              totalTrades: results.total_trades || 0,
              openPositions: results.open_positions || 0,
              winRate: results.win_rate || 0,
              pnl: results.pnl || 0,
              maxDrawdown: results.max_drawdown || 0,
              sharpeRatio: results.sharpe_ratio || null,
              profitFactor: results.profit_factor || 0,
              trades: results.trades || [],
              equityCurve: results.equity_curve || [],
            },
            // Clear optimization state
            optimizationStatus: 'idle',
            optimizationResults: null,
            optimizationError: null,
            selectedHistoryBacktestId: backtestId,
            backtestError: null,
          })
        }
      } else if (response.data.status === 'error') {
        set({
          backtestStatus: 'error',
          backtestError: response.data.error || 'Backtest failed',
          selectedHistoryBacktestId: backtestId,
        })
      }
    } catch (error: any) {
      console.error('Failed to load backtest result:', error)
      set({
        backtestError: error.response?.data?.detail || 'Failed to load backtest',
      })
    }
  },

  deleteBacktestResult: async (backtestId: string) => {
    try {
      if (backtestId.startsWith('mbt_')) {
        await axios.delete(`${API_BASE}/backtest/multi-results/${backtestId}`)
      } else {
        await axios.delete(`${API_BASE}/backtest/results/${backtestId}`)
      }
      // Refresh history
      await get().fetchBacktestHistory()
      // Clear results if viewing deleted backtest
      if (get().selectedHistoryBacktestId === backtestId) {
        if (backtestId.startsWith('mbt_')) {
          get().clearMultiBacktestResults()
        } else {
          get().clearBacktestResults()
        }
      }
    } catch (error) {
      console.error('Failed to delete backtest:', error)
    }
  },

  setSelectedHistoryBacktestId: (id: string | null) => {
    set({ selectedHistoryBacktestId: id })
  },

  // Optimization actions
  fetchParamGrid: async () => {
    try {
      const response = await axios.get(`${API_BASE}/strategies/param-grid`)
      set({
        optimizationParamGrid: response.data.param_grid,
        optimizationTotalCombinations: response.data.total_combinations,
      })
    } catch (error: any) {
      console.error('Failed to fetch param grid:', error)
    }
  },

  runOptimization: async (paramGrid?: Record<string, any[]>) => {
    const state = get()
    if (!state.backtestConfig || !state.selectedStrategyId || !state.selectedPreset) {
      set({ optimizationError: 'Please select a strategy and configure instrument' })
      return
    }

    set({
      optimizationStatus: 'running',
      optimizationProgress: null,
      optimizationResults: null,
      optimizationError: null,
      optimizationParetoFront: null,
    })

    try {
      // Ensure strategy is selected
      await axios.post(`${API_BASE}/strategies/select`, {
        strategy_id: state.selectedStrategyId,
        preset: state.selectedPreset,
      })

      // Start in optimizer mode (with optional filtered param grid)
      // Use multi-instrument list if available, fall back to single instrument
      const instrList = state.optimizationInstruments.length > 0
        ? state.optimizationInstruments
        : [state.backtestConfig.instrument]
      const payload: Record<string, any> = {
        mode: 'optimizer',
        instrument: instrList[0],
        instruments: instrList,
        optimization_method: state.optimizationMethod,
        n_trials: state.optunaConfig.n_trials,
        walk_forward_splits: state.optunaConfig.walk_forward_splits,
        train_ratio: state.optunaConfig.train_ratio,
        start_date: state.backtestConfig.startDate || null,
        end_date: state.backtestConfig.endDate || null,
        ...(state.paramOverrides && Object.keys(state.paramOverrides).length > 0
          ? { param_overrides: state.paramOverrides }
          : {}),
      }
      if (paramGrid) {
        payload.param_grid = paramGrid
      }
      const response = await axios.post(`${API_BASE}/strategies/start`, payload)

      const backtestId = response.data.backtest_id
      if (backtestId) {
        // Poll for completion
        const pollResults = async () => {
          try {
            const resultsResponse = await axios.get(`${API_BASE}/backtest/results/${backtestId}`)
            if (resultsResponse.data.status === 'completed') {
              const results = resultsResponse.data.results || {}
              if (results.type === 'optimization') {
                set({
                  optimizationStatus: 'completed',
                  optimizationResults: results.results || [],
                  optimizationProgress: {
                    current: results.total_combinations || 0,
                    total: results.total_combinations || 0,
                  },
                  optimizationParetoFront: results.pareto_front || null,
                })
              }
            } else if (resultsResponse.data.status === 'error') {
              set({
                optimizationStatus: 'error',
                optimizationError: resultsResponse.data.error || 'Optimization failed',
              })
            } else {
              setTimeout(pollResults, 2000)
            }
          } catch (pollError: any) {
            if (pollError.response?.status === 404) {
              setTimeout(pollResults, 2000)
              return
            }
            set({
              optimizationStatus: 'error',
              optimizationError: pollError.response?.data?.detail || 'Failed to fetch results',
            })
          }
        }

        setTimeout(pollResults, 3000)
      }
    } catch (error: any) {
      set({
        optimizationStatus: 'error',
        optimizationError: error.response?.data?.detail || 'Failed to start optimization',
      })
    }
  },

  clearOptimizationResults: () => {
    set({
      optimizationStatus: 'idle',
      optimizationProgress: null,
      optimizationResults: null,
      optimizationError: null,
      optimizationParetoFront: null,
    })
  },

  updateOptimizationProgress: (data) => {
    set((state) => ({
      optimizationProgress: { current: data.current, total: data.total },
      ...(data.result ? {
        optimizationResults: [...(state.optimizationResults ?? []), data.result],
      } : {}),
    }))
  },

  setOptimizationMethod: (method) => {
    set({ optimizationMethod: method })
  },

  setOptunaConfig: (config) => {
    set((state) => ({
      optunaConfig: { ...state.optunaConfig, ...config },
    }))
  },

  setOptimizationInstruments: (ids) => {
    set({ optimizationInstruments: ids })
  },

  // Data Catalog actions
  fetchDataCatalogConfig: async () => {
    set({ dataCatalogLoading: true, dataCatalogError: null })
    try {
      const response = await axios.get(`${API_BASE}/config/data-catalog`)
      set({
        dataCatalogPath: response.data.path,
        dataCatalogExists: response.data.exists,
        dataCatalogDefaultPath: response.data.default_path,
        dataCatalogLoading: false,
      })
    } catch (error: any) {
      console.error('Failed to fetch data catalog config:', error)
      set({
        dataCatalogLoading: false,
        dataCatalogError: error.response?.data?.detail || 'Failed to fetch data catalog config',
      })
    }
  },

  setDataCatalogPath: async (path: string): Promise<boolean> => {
    set({ dataCatalogLoading: true, dataCatalogError: null })
    try {
      const response = await axios.put(`${API_BASE}/config/data-catalog`, { path })
      set({
        dataCatalogPath: response.data.path,
        dataCatalogExists: true,
        dataCatalogLoading: false,
      })
      // Refresh instruments after path change
      await get().fetchBacktestInstruments()
      return true
    } catch (error: any) {
      console.error('Failed to set data catalog path:', error)
      set({
        dataCatalogLoading: false,
        dataCatalogError: error.response?.data?.detail || 'Failed to set data catalog path',
      })
      return false
    }
  },

  scanDataCatalog: async () => {
    set({ dataCatalogLoading: true, dataCatalogError: null })
    try {
      const response = await axios.post(`${API_BASE}/config/data-catalog/scan`)
      set({
        backtestInstruments: response.data.instruments || [],
        dataCatalogPath: response.data.catalog_path,
        dataCatalogLoading: false,
      })
    } catch (error: any) {
      console.error('Failed to scan data catalog:', error)
      set({
        dataCatalogLoading: false,
        dataCatalogError: error.response?.data?.detail || 'Failed to scan data catalog',
      })
    }
  },

  runDataAudit: async () => {
    set({
      dataAuditRunning: true,
      dataAuditError: null,
      dataAuditSummary: null,
      dataAuditProgress: { step: 'scan', current: 0, total: 0 },
    })
    try {
      const response = await axios.post(`${API_BASE}/config/data-catalog/audit`)
      // Only the data_audit_complete WS message clears dataAuditRunning.
      set({ dataAuditProgress: { step: 'scan', current: 0, total: response.data.total || 0 } })
    } catch (error: any) {
      console.error('Failed to start data-quality audit:', error)
      set({
        dataAuditRunning: false,
        dataAuditProgress: null,
        dataAuditError: error.response?.data?.detail || 'Failed to start data-quality audit',
      })
    }
  },

  // Market Analysis actions
  fetchMarketAnalysis: async (forceRefresh: boolean = false) => {
    const current = get().marketAnalysis
    if (!forceRefresh && current) return

    set({ marketAnalysisLoading: true, marketAnalysisError: null })
    try {
      const response = await axios.get(`${API_BASE}/backtest/market-analysis`, {
        params: { refresh: forceRefresh },
      })
      set({
        marketAnalysis: response.data,
        marketAnalysisLoading: false,
      })
    } catch (error: any) {
      console.error('Failed to fetch market analysis:', error)
      set({
        marketAnalysisLoading: false,
        marketAnalysisError: error.response?.data?.detail || 'Failed to compute market analysis',
      })
    }
  },

  clearMarketAnalysis: () => {
    set({ marketAnalysis: null, marketAnalysisError: null })
  },

  // Multi-backtest actions
  setMultiBacktestInstruments: (ids: string[]) => {
    set({ multiBacktestInstruments: ids })
  },

  setMultiBacktestWorkers: (n: number) => {
    set({
      multiBacktestWorkers: Math.max(1, Math.min(16, Math.round(n) || 1)),
      multiBacktestWorkersTouched: true,
    })
  },

  setMultiBacktestStrategyPresets: (presets: Array<{ strategy_id: string; preset: string }>) => {
    set({ multiBacktestStrategyPresets: presets })
  },

  runMultiBacktest: async () => {
    const state = get()
    const hasStrategyPresets = state.multiBacktestStrategyPresets.length > 0
    if (
      state.multiBacktestInstruments.length === 0 ||
      (!hasStrategyPresets && (!state.selectedStrategyId || !state.selectedPreset))
    ) {
      set({ multiBacktestError: 'Please select instruments and at least one strategy' })
      return
    }
    if (!hasStrategyPresets) {
      const mismatch = paramEditsMismatch(state)
      if (mismatch) {
        set({ multiBacktestError: mismatch })
        return
      }
    }

    set({
      multiBacktestStatus: 'running',
      multiBacktestProgress: null,
      multiBacktestResults: null,
      multiBacktestError: null,
    })

    try {
      // Ensure a strategy is active (needed by registry even for multi-strategy)
      const sid = hasStrategyPresets ? state.multiBacktestStrategyPresets[0].strategy_id : state.selectedStrategyId!
      const preset = hasStrategyPresets ? state.multiBacktestStrategyPresets[0].preset : state.selectedPreset!
      await axios.post(`${API_BASE}/strategies/select`, {
        strategy_id: sid,
        preset: preset,
      })

      const payload: any = {
        instruments: state.multiBacktestInstruments,
        max_workers: state.multiBacktestWorkers,
      }
      if (hasStrategyPresets) {
        // The edits are a diff against the LOADED preset, so they ride only on
        // the chip(s) equal to it; every other strategy/preset runs as-is.
        const ov = state.paramOverrides
        const base = state.activeStrategy
        const hasEdits = !!ov && Object.keys(ov).length > 0 && !!base
        payload.strategy_presets = state.multiBacktestStrategyPresets.map(c =>
          hasEdits && c.strategy_id === base!.strategy_id && c.preset === base!.preset_name
            ? { ...c, param_overrides: ov }
            : c
        )
      } else if (state.paramOverrides && Object.keys(state.paramOverrides).length > 0) {
        // Only in the "active strategy × instruments" case: the overrides are a
        // diff against THIS preset, so they are meaningless (and can be invalid
        // keys) for the other strategies in a multi-strategy sweep.
        payload.param_overrides = state.paramOverrides
      }

      const response = await axios.post(`${API_BASE}/strategies/start-multi`, payload)

      const multiId = response.data.multi_backtest_id
      set({ multiBacktestId: multiId })

      // Poll for results
      const pollResults = async () => {
        try {
          const res = await axios.get(`${API_BASE}/backtest/multi-results/${multiId}`)
          if (res.data.status === 'completed') {
            set({
              multiBacktestStatus: 'completed',
              multiBacktestResults: res.data,
              multiBacktestProgress: null,
            })
            // Refresh backtest history
            get().fetchBacktestHistory()
          } else if (res.data.status === 'error') {
            set({
              multiBacktestStatus: 'error',
              multiBacktestError: 'Multi-backtest failed',
            })
          } else {
            setTimeout(pollResults, 2000)
          }
        } catch (err: any) {
          if (err.response?.status === 404) {
            setTimeout(pollResults, 2000)
            return
          }
          set({
            multiBacktestStatus: 'error',
            multiBacktestError: err.response?.data?.detail || 'Failed to fetch results',
          })
        }
      }

      setTimeout(pollResults, 2000)
    } catch (error: any) {
      set({
        multiBacktestStatus: 'error',
        multiBacktestError: error.response?.data?.detail || 'Failed to start multi-backtest',
      })
    }
  },

  fetchMultiBacktestResults: async (id: string) => {
    try {
      const res = await axios.get(`${API_BASE}/backtest/multi-results/${id}`)
      set({
        multiBacktestStatus: 'completed',
        multiBacktestResults: res.data,
        multiBacktestProgress: null,
      })
    } catch (error) {
      console.error('Failed to fetch multi-backtest results:', error)
    }
  },

  clearMultiBacktestResults: () => {
    set({
      multiBacktestStatus: 'idle',
      multiBacktestId: null,
      multiBacktestProgress: null,
      multiBacktestResults: null,
      multiBacktestError: null,
    })
  },
}))
